import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useUser } from '@/contexts/UserContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/useToast';
import { User, Mail, Phone, MapPin, Shield, Warehouse, Key, Building } from 'lucide-react';
import { useCustomers } from '@/hooks/useCustomers';
import { useWarehouses } from '@/hooks/useWarehouses';
import { useShipments } from '@/hooks/useShipments';

export default function CustomerProfile() {
  const { user } = useUser();
  const { t } = useLanguage();
  const { toast } = useToast();

  const { customers: apiCustomers, updateCustomer } = useCustomers();
  const { warehouses: apiWarehouses } = useWarehouses();
  const { shipments: apiShipments } = useShipments();

  const customerData = (apiCustomers || []).find((c: any) => c.code === user?.customerCode);
  const warehouses = apiWarehouses || [];
  const shipments = (apiShipments || []).filter((s: any) =>
    (s.customer_code || s.customerCode) === user?.customerCode
  );
  const warehouseData = warehouses.find((w: any) => w.code === user?.warehouseCode);

  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    destinationWarehouse: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [newDefaultUsername, setNewDefaultUsername] = useState('');
  const [newDefaultPassword, setNewDefaultPassword] = useState('');

  // Load current default credentials for admin
  const [currentDefaultUsername, setCurrentDefaultUsername] = useState('');
  const [currentDefaultPassword, setCurrentDefaultPassword] = useState('');

  // Edit state for default credentials section
  const [isEditingDefault, setIsEditingDefault] = useState(false);

  useEffect(() => {
    if (!user || !customerData) return;

    // Set form data from customer data
    if (user.role === 'customer' && customerData) {
      setFormData({
        name: customerData.name || '',
        email: customerData.email || '',
        phone: customerData.phone || '',
        address: customerData.address || '',
        city: customerData.city || '',
        state: customerData.state || '',
        country: customerData.country || '',
        destinationWarehouse: customerData.destination_warehouse || customerData.destinationWarehouse || ''
      });
    }
  }, [user, customerData]);

  // Old data loading removed - now using API hooks

  const handleSave = () => {
    if (!user?.customerCode) return;

    updateCustomer({
      code: user.customerCode,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      country: formData.country,
      destination_warehouse: formData.destinationWarehouse
    });

    toast({
      title: "Profile Updated",
      description: "Your profile information has been saved successfully.",
    });

    setIsEditing(false);
  };

  const handleSaveDefaultCredentials = () => {
    // Get users from localStorage
    const savedUsers = localStorage.getItem('shiphub-users');
    const users = savedUsers ? JSON.parse(savedUsers) : [];

    // Update default admin user
    const updatedUsers = users.map((u: any) => {
      if (u.username === 'admin') {
        return {
          ...u,
          username: newDefaultUsername || 'admin',
          password: newDefaultPassword || 'admin123'
        };
      }
      return u;
    });

    // Save updated users
    localStorage.setItem('shiphub-users', JSON.stringify(updatedUsers));

    // Update current user context if it's the admin
    if (user?.username === 'admin') {
      login(newDefaultUsername || 'admin', newDefaultPassword || 'admin123');
    }

    toast({
      title: t('defaultCredentialsUpdated'),
      description: t('defaultCredentialsUpdatedDesc'),
    });

    // Clear form
    setNewDefaultUsername('');
    setNewDefaultPassword('');
  };

  const handlePasswordChange = () => {
    // Validate passwords
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast({
        title: "Validation Error",
        description: "All password fields are required.",
        variant: "destructive"
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast({
        title: "Validation Error",
        description: "New password must be at least 6 characters.",
        variant: "destructive"
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Validation Error",
        description: "New password and confirmation do not match.",
        variant: "destructive"
      });
      return;
    }

    // Get users from localStorage
    const savedUsers = localStorage.getItem('shiphub-users');
    const users = savedUsers ? JSON.parse(savedUsers) : [];

    // Find current user and verify current password
    const currentUser = users.find((u: any) => u.username === user?.username);
    if (!currentUser || currentUser.password !== passwordData.currentPassword) {
      toast({
        title: "Authentication Error",
        description: "Current password is incorrect.",
        variant: "destructive"
      });
      return;
    }

    // Update password
    const updatedUsers = users.map((u: any) => {
      if (u.username === user?.username) {
        return { ...u, password: passwordData.newPassword };
      }
      return u;
    });

    localStorage.setItem('shiphub-users', JSON.stringify(updatedUsers));

    toast({
      title: "Password Updated",
      description: "Your password has been changed successfully.",
    });

    setIsChangingPassword(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  if (!user) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p>Please log in to view your profile.</p>
        </div>
      </Layout>
    );
  }

  if (user.role === 'customer' && !customerData) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p>Loading customer profile...</p>
        </div>
      </Layout>
    );
  }

  if (user.role === 'warehouse' && !warehouseData) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p>Loading warehouse profile...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('myProfile')}</h1>
          <p className="text-gray-600">
            {user?.role === 'admin' ? 'Admin Account Information' :
             user?.role === 'warehouse' ? 'Warehouse Manager Account' :
             'Customer Account Information'}
          </p>
        </div>

        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span>{t('accountInformation')}</span>
            </CardTitle>
            <CardDescription>
              {user?.role === 'admin' ? t('administratorAccountDetails') :
               user?.role === 'warehouse' ? t('warehouseManagerAccountDetails') :
               t('customerAccountDetails')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('username')}</Label>
                <Input
                  value={user?.role === 'admin' ? currentDefaultUsername : (user?.username || '')}
                  disabled={!isEditing && user?.role !== 'admin'}
                  className={!isEditing && user?.role !== 'admin' ? 'bg-gray-100' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('accountType')}</Label>
                <Input value={user?.role || ''} disabled className="bg-gray-100" />
              </div>
              {user?.role === 'customer' && (
                <>
                  <div className="space-y-2">
                    <Label>{t('customerCode')}</Label>
                    <Input value={user?.customerCode || ''} disabled className="bg-gray-100" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('status')}</Label>
                    <Input value={customerData?.status || 'Active'} disabled className="bg-gray-100" />
                  </div>
                </>
              )}
              {user?.role === 'warehouse' && (
                <>
                  <div className="space-y-2">
                    <Label>{t('warehouseCode')}</Label>
                    <Input value={user?.warehouseCode || ''} disabled className="bg-gray-100" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('warehouseName')}</Label>
                    <Input value={user?.warehouseName || ''} disabled className="bg-gray-100" />
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Personal Information - Only for customers */}
        {user?.role === 'customer' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center space-x-2">
                  <User className="h-5 w-5" />
                  <span>Personal Information</span>
                </CardTitle>
                <CardDescription>Your contact and address details</CardDescription>
              </div>
              {!(isEditing || (user?.role === 'admin' && !isEditingDefault)) && (
                <Button onClick={() => setIsEditing(true)} variant="outline">
                  Edit Profile
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={!isEditing}
                  className={!isEditing ? 'bg-gray-100' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!isEditing}
                  className={!isEditing ? 'bg-gray-100' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{t('phone')}</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  disabled={!isEditing}
                  className={!isEditing ? 'bg-gray-100' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">{t('address')}</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  disabled={!isEditing}
                  className={!isEditing ? 'bg-gray-100' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">{t('city')}</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  disabled={!isEditing}
                  className={!isEditing ? 'bg-gray-100' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">{t('state')}</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  disabled={!isEditing}
                  className={!isEditing ? 'bg-gray-100' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">{t('country')}</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  disabled={!isEditing}
                  className={!isEditing ? 'bg-gray-100' : ''}
                />
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Shipping Preferences - Only for customers */}
        {user?.role === 'customer' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center space-x-2">
                  <Warehouse className="h-5 w-5" />
                  <span>Shipping Preferences</span>
                </CardTitle>
                <CardDescription>Your default shipping destination</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="destinationWarehouse">Preferred Destination Warehouse</Label>
              {isEditing ? (
                <Select
                  value={formData.destinationWarehouse}
                  onValueChange={(value) => setFormData({ ...formData, destinationWarehouse: value })}
                >
                  <SelectTrigger id="destinationWarehouse">
                    <SelectValue placeholder="Select a warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map((warehouse) => (
                      <SelectItem key={warehouse.id} value={warehouse.code}>
                        {warehouse.name} ({warehouse.code}) - {warehouse.city}, {warehouse.country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={
                    warehouses.find((w) => w.code === formData.destinationWarehouse)?.name ||
                    formData.destinationWarehouse ||
                    'Not set'
                  }
                  disabled
                  className="bg-gray-100"
                />
              )}
              <p className="text-xs text-gray-500">
                This warehouse will be used as the default destination for your shipments
              </p>
            </div>

            {isEditing && (
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    name: customerData.name || '',
                    email: customerData.email || '',
                    phone: customerData.phone || '',
                    address: customerData.address || '',
                    city: customerData.city || '',
                    state: customerData.state || '',
                    country: customerData.country || '',
                    destinationWarehouse: customerData.destinationWarehouse || ''
                  });
                }}>
                  Cancel
                </Button>
                <Button onClick={handleSave}>
                  Save Changes
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Password Change */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center space-x-2">
                  <Key className="h-5 w-5" />
                  <span>Change Password</span>
                </CardTitle>
                <CardDescription>Update your account password</CardDescription>
              </div>
              {!isChangingPassword && (
                <Button onClick={() => setIsChangingPassword(true)} variant="outline">
                  Change Password
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isChangingPassword ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    placeholder="Enter your current password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    placeholder="Enter your new password (min 6 characters)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    placeholder="Confirm your new password"
                  />
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => {
                    setIsChangingPassword(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}>
                    Cancel
                  </Button>
                  <Button onClick={handlePasswordChange}>
                    Update Password
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Key className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Your password is securely stored. Click "Change Password" to update it.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Statistics - Only for customers */}
        {user?.role === 'customer' && customerData && (
        <Card>
          <CardHeader>
            <CardTitle>Account Statistics</CardTitle>
            <CardDescription>Your shipping activity summary</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-900">{shipments.length}</p>
                <p className="text-sm text-blue-700">Total Shipments</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-900">{customerData.status}</p>
                <p className="text-sm text-green-700">Account Status</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-900">{customerData.createdAt}</p>
                <p className="text-sm text-purple-700">Member Since</p>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <p className="text-2xl font-bold text-orange-900">{customerData.code}</p>
                <p className="text-sm text-orange-700">Customer Code</p>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Warehouse Information - Only for warehouse managers */}
        {user?.role === 'warehouse' && warehouseData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Building className="h-5 w-5" />
              <span>Warehouse Information</span>
            </CardTitle>
            <CardDescription>Your assigned warehouse details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-900">{warehouseData.name}</p>
                <p className="text-sm text-blue-700">Warehouse Name</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-900">{warehouseData.code}</p>
                <p className="text-sm text-green-700">Warehouse Code</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-900">{warehouseData.city}</p>
                <p className="text-sm text-purple-700">Location</p>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <p className="text-2xl font-bold text-orange-900">{warehouseData.status}</p>
                <p className="text-sm text-orange-700">Status</p>
              </div>
              <div className="text-center p-4 bg-pink-50 rounded-lg">
                <p className="text-2xl font-bold text-pink-900">{warehouseData.capacity}</p>
                <p className="text-sm text-pink-700">Capacity</p>
              </div>
              <div className="text-center p-4 bg-indigo-50 rounded-lg">
                <p className="text-2xl font-bold text-indigo-900">{warehouseData.type}</p>
                <p className="text-sm text-indigo-700">Type</p>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Admin Default Credentials - Only for admin users */}
        {user?.role === 'admin' && (
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span>Default Credentials</span>
            </div>
            <CardDescription>Change the default admin login credentials</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="default-username">Default Username</Label>
              <Input
                id="default-username"
                value="admin"
                disabled
                className="bg-gray-100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="default-password">Default Password</Label>
              <Input
                id="default-password"
                value="admin123"
                disabled
                className="bg-gray-100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-default-username">New Default Username</Label>
              <Input
                id="new-default-username"
                placeholder="Enter new default username"
                onChange={(e) => setNewDefaultUsername(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-default-password">New Default Password</Label>
              <Input
                id="new-default-password"
                type="password"
                placeholder="Enter new default password"
                onChange={(e) => setNewDefaultPassword(e.target.value)}
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => {
                setNewDefaultUsername('');
                setNewDefaultPassword('');
              }}>
                Cancel
              </Button>
              <Button onClick={handleSaveDefaultCredentials}>
                Save Default Credentials
              </Button>
            </div>
          </CardContent>
        </Card>
        )}
      </div>
    </Layout>
  );
}
